import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { clampTooltipX } from '../utils/tooltipClamp';

interface EloHistoryEntry {
  matchup_id: number;
  elo_before: number;
  elo_after: number;
  elo_change: number;
  rank_before: number;
  rank_after: number;
  opponent_id: number;
  opponent_name: string;
  opponent_avatar: string | null;
  result: 'win' | 'loss' | 'draw';
  created_at: string;
}

interface EloHistoryResponse {
  player_name: string;
  player_avatar: string | null;
  history: EloHistoryEntry[];
}

interface Props {
  selectedPlayerId: number | null;
  comparePlayerId?: number | null;
  mode?: 'elo' | 'rank';
}

const CHART_H = 180;
const PAD_T = 28;
const PAD_B = 10;
const AV_MARGIN = 40;
const DOT_FADE_MS = 300;
const LINE_DRAW_MS = 700;

export default function EloHistoryChart({ selectedPlayerId, comparePlayerId, mode = 'elo' }: Props) {
  const { token } = useAuth();
  const [dataA, setDataA] = useState<EloHistoryResponse | null>(null);
  const [dataB, setDataB] = useState<EloHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState<{ entry: EloHistoryEntry; x: number; y: number; playerName?: string } | null>(null);
  const [isMobile] = useState(() => window.innerWidth <= 700);
  const [visibleDotsA, setVisibleDotsA] = useState(0);
  const [visibleDotsB, setVisibleDotsB] = useState(0);
  const [animLineIdxA, setAnimLineIdxA] = useState(-1);
  const [animLineIdxB, setAnimLineIdxB] = useState(-1);
  const [containerWidth, setContainerWidth] = useState(400);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgWrapRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const avPx = isMobile ? 27 : 39;
  const isCompare = !!comparePlayerId;

  const updateWidth = useCallback(() => {
    const el = svgWrapRef.current || containerRef.current;
    if (el) setContainerWidth(el.clientWidth);
  }, []);

  useEffect(() => {
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [updateWidth]);

  const clearTimers = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };

  // Animation helper
  const runAnimation = (historyLen: number, setDots: (n: number) => void, setLine: (n: number) => void) => {
    const total = Math.min(historyLen, isMobile ? 8 : 10);
    if (total === 0) return;
    let step = 0;
    const runStep = () => {
      if (step === 0) { setDots(1); step++; if (total > 1) timersRef.current.push(setTimeout(runStep, DOT_FADE_MS)); }
      else {
        setLine(step - 1);
        timersRef.current.push(setTimeout(() => { setLine(-1); setDots(step + 1); step++; if (step < total) timersRef.current.push(setTimeout(runStep, DOT_FADE_MS)); }, LINE_DRAW_MS));
      }
    };
    timersRef.current.push(setTimeout(runStep, 400));
  };

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setVisibleDotsA(0); setVisibleDotsB(0);
    setAnimLineIdxA(-1); setAnimLineIdxB(-1);
    setTooltip(null); clearTimers();
    const pidA = selectedPlayerId ?? 'me';
    const pidB = comparePlayerId ?? null;
    Promise.all([
      api.get(`/duel/elo-history/${pidA}`, token),
      pidB ? api.get(`/duel/elo-history/${pidB}`, token) : Promise.resolve(null),
    ]).then(([resA, resB]) => {
      if (resA && resA.history) { setDataA(resA); runAnimation(resA.history.length, setVisibleDotsA, setAnimLineIdxA); }
      if (resB && resB.history) { setDataB(resB); runAnimation(resB.history.length, setVisibleDotsB, setAnimLineIdxB); }
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => clearTimers();
  }, [token, selectedPlayerId, comparePlayerId, isMobile]);

  const maxGames = isMobile ? 8 : 10;
  const allEntriesA = useMemo(() => dataA ? dataA.history.slice(-maxGames) : [], [dataA, maxGames]);
  const allEntriesB = useMemo(() => dataB ? dataB.history.slice(-maxGames) : [], [dataB, maxGames]);
  const entriesA = allEntriesA.slice(0, visibleDotsA);
  const entriesB = allEntriesB.slice(0, visibleDotsB);

  // Close tooltip on scroll or outside click (mobile + desktop)
  useEffect(() => {
    if (!tooltip) return;
    const close = () => setTooltip(null);
    window.addEventListener('scroll', close, { once: true, capture: true });
    window.addEventListener('resize', close, { once: true });
    document.addEventListener('click', close, { once: true });
    return () => {
      window.removeEventListener('scroll', close as any);
      window.removeEventListener('resize', close);
      document.removeEventListener('click', close);
    };
  }, [tooltip]);

  if (loading) return <div className="elo-chart-loading">Loading OnFire history…</div>;
  if (!dataA || allEntriesA.length === 0) {
    const isSelf = selectedPlayerId === null;
    return (
      <div className="elo-chart-empty">
        <span className="elo-chart-empty-icon">🔥</span>
        {isSelf
          ? 'Play your first duel to see your OnFire history'
          : `${dataA?.player_name ?? 'This player'} hasn't played any duels yet`
        }
      </div>
    );
  }

  const svgW = containerWidth;
  const usableH = CHART_H - PAD_T - PAD_B;

  // For rank mode: use rank_before/rank_after, invert axis (lower rank = higher)
  const allEloAll = mode === 'rank'
    ? [...allEntriesA.flatMap(e => [e.rank_before, e.rank_after]), ...allEntriesB.flatMap(e => [e.rank_before, e.rank_after])]
    : [...allEntriesA.flatMap(e => [e.elo_before, e.elo_after]), ...allEntriesB.flatMap(e => [e.elo_before, e.elo_after])];
  const lo = Math.min(...allEloAll);
  const hi = Math.max(...allEloAll);
  const range = hi - lo || 1;
  const yMin = Math.floor(lo - range * 0.08);
  const yMax = Math.ceil(hi + range * 0.08);

  const totalA = allEntriesA.length || 1;
  const totalB = allEntriesB.length || 1;
  const xOfA = (i: number) => {
    if (totalA === 1) return svgW / 2;
    return AV_MARGIN + (i / (totalA - 1)) * (svgW - 2 * AV_MARGIN);
  };
  const xOfB = (i: number) => {
    if (totalB === 1) return svgW / 2;
    return AV_MARGIN + (i / (totalB - 1)) * (svgW - 2 * AV_MARGIN);
  };
  const yOf = (val: number) => {
    // For rank: invert axis (rank 1 at top)
    if (mode === 'rank') {
      return PAD_T + ((val - yMin) / (yMax - yMin)) * usableH;
    }
    return PAD_T + usableH - ((val - yMin) / (yMax - yMin)) * usableH;
  };

  const handleClick = (e: EloHistoryEntry, i: number, ev: React.MouseEvent, playerName?: string) => {
    ev.stopPropagation();
    // Use the clicked element's position directly — no SVG coordinate math needed
    const rect = (ev.currentTarget as SVGGElement).getBoundingClientRect();
    const cx = clampTooltipX(rect.left + rect.width / 2, 220, true);
    const cy = Math.min(rect.top, window.innerHeight - 120);
    setTooltip(prev => prev?.entry === e ? null : { entry: e, x: cx, y: cy - 12, playerName });
  };

  const ticks = Array.from({ length: 6 }, (_, i) => Math.round(yMin + (i / 5) * (yMax - yMin)));

  // Helper to render a single player's line + dots
  const renderPlayerLine = (
    entries: EloHistoryEntry[], allEntries: EloHistoryEntry[],
    visibleDots: number, _animLineIdx: number,
    xOf: (i: number) => number, clipId: string, playerName: string, isSecond: boolean
  ) => {
    const dots = entries;
    const lineColor = isSecond ? '#60a5fa' : '#ff8c00';
    const stroke = isSecond ? 'url(#elo-blue-grad)' : 'url(#elo-fire-grad)';
    return (
      <>
        {/* Line segments */}
        {/* Area – rendered first so lines and dots appear on top */}
        {dots.length > 1 && (
          <path
            d={dots.map((e, i) => `${i === 0 ? 'M' : 'L'} ${xOf(i)} ${yOf(mode === 'rank' ? e.rank_after : e.elo_after)}`).join(' ')
              + ` L ${xOf(dots.length - 1)} ${PAD_T + usableH} L ${xOf(0)} ${PAD_T + usableH} Z`}
            fill="url(#elo-area-g)"
          />
        )}
        {/* Lines – always solid, no dash animation */}
        {dots.length > 1 && dots.slice(0, -1).map((e, i) => {
          const x1 = xOf(i); const y1 = yOf(mode === 'rank' ? e.rank_after : e.elo_after);
          const x2 = xOf(i + 1); const y2 = yOf(mode === 'rank' ? dots[i + 1].rank_after : dots[i + 1].elo_after);
          return (
            <line key={`${clipId}-seg-${i}`} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={lineColor} strokeWidth="5" strokeLinecap="round"
            />
          );
        })}
        {/* Points */}
        {dots.map((e, i) => {
          const cx = xOf(i); const cy = yOf(mode === 'rank' ? e.rank_after : e.elo_after);
          const ring = e.result === 'win' ? '#22c55e' : e.result === 'loss' ? '#ef4444' : '#666';
          return (
            <g key={`${clipId}-pt-${i}`} style={{ cursor: 'pointer', animation: 'elo-point-in 0.3s ease both' }}
              onClick={(ev) => handleClick(e, i, ev, playerName)}
              onMouseEnter={(ev) => { if (!isMobile) { ev.stopPropagation(); const rect = (ev.currentTarget as SVGGElement).getBoundingClientRect(); const cx = clampTooltipX(rect.left + rect.width / 2, 220, true); const cy = Math.min(rect.top, window.innerHeight - 120); setTooltip({ entry: e, x: cx, y: cy - 12, playerName }); } }}
              onMouseLeave={() => { if (!isMobile) setTooltip(null); }}
            >
              <circle cx={cx} cy={cy} r={avPx * 0.55 + 6} fill="transparent" />
              <circle cx={cx} cy={cy} r={avPx * 0.55 + 1.5} fill={ring} style={{ pointerEvents: 'none' }} />
              {!e.opponent_avatar ? (
                <>
                  <circle cx={cx} cy={cy} r={avPx * 0.5} fill="#2a2e42" style={{ pointerEvents: 'none' }} />
                  <svg x={cx - avPx * 0.35} y={cy - avPx * 0.35} width={avPx * 0.7} height={avPx * 0.7} viewBox="0 0 640 512" style={{ pointerEvents: 'none' }}>
                    <path fill="#888" d="M352 0c0-17.7-14.3-32-32-32S288-17.7 288 0l0 64-96 0c-53 0-96 43-96 96l0 224c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-224c0-53-43-96-96-96l-96 0 0-64zM160 368c0-13.3 10.7-24 24-24l32 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-32 0c-13.3 0-24-10.7-24-24zm120 0c0-13.3 10.7-24 24-24l32 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-32 0c-13.3 0-24-10.7-24-24zm120 0c0-13.3 10.7-24 24-24l32 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-32 0c-13.3 0-24-10.7-24-24zM224 176a48 48 0 1 1 0 96 48 48 0 1 1 0-96zm144 48a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zM64 224c0-17.7-14.3-32-32-32S0 206.3 0 224l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96zm544-32c-17.7 0-32 14.3-32 32l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96c0-17.7-14.3-32-32-32z"/>
                  </svg>
                </>
              ) : (
                <>
                  <clipPath id={`ec${clipId}-${i}`}><circle cx={cx} cy={cy} r={avPx * 0.5} /></clipPath>
                  <image href={e.opponent_avatar!} x={cx - avPx * 0.5} y={cy - avPx * 0.5} width={avPx} height={avPx}
                    clipPath={`url(#ec${clipId}-${i})`} preserveAspectRatio="xMidYMid slice" style={{ pointerEvents: 'none' }} />
                </>
              )}
              <text x={cx} y={cy + avPx * 0.55 + 12} textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="inherit"
                fill={ring} style={{ pointerEvents: 'none' }}>
                {mode === 'rank'
                  ? `#${e.rank_after}`
                  : e.elo_after
                }
              </text>
            </g>
          );
        })}
      </>
    );
  };

  return (
    <div className="elo-chart-wrapper" ref={containerRef} onClick={() => setTooltip(null)}>
      <div className="elo-chart-header">
        {(() => {
          // Calculate unique weeks spanned by history
          const entries = allEntriesA;
          let weekCount = 1;
          if (entries.length > 0) {
            // Each matchup = 1 week — count unique matchup_ids
            const uniqueMatchups = new Set(entries.map(e => e.matchup_id).filter(Boolean));
            weekCount = uniqueMatchups.size || entries.length;
          }
          const title = mode === 'rank' ? `Rank History of ${dataA?.player_name ?? ''}` : `OnFire Points (last ${weekCount} week${weekCount !== 1 ? 's' : ''})`;
          return <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#8890a4', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>;
        })()}
      </div>

      <div ref={svgWrapRef} style={{ width: '100%' }}>
        <svg viewBox={`0 0 ${svgW} ${CHART_H}`} className="elo-chart-svg" preserveAspectRatio="xMinYMin meet">
          <defs>
            <linearGradient id="elo-fire-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#a16207" />
              <stop offset="30%" stopColor="#eab308" />
              <stop offset="60%" stopColor="#ff8c00" />
              <stop offset="100%" stopColor="#ff4500" />
            </linearGradient>
            <linearGradient id="elo-blue-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#1e40af" />
              <stop offset="50%" stopColor="#2573d3" />
              <stop offset="100%" stopColor="#60a5fa" />
            </linearGradient>
            <linearGradient id="elo-area-g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ff8c00" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#ff8c00" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Grid */}
          {ticks.map(v => {
            const y = yOf(v);
            return (
              <g key={v}>
                <line x1={0} y1={y} x2={svgW} y2={y} stroke="#1e2130" strokeWidth="0.5" />
                <text x={2} y={y + 2} fontSize="11" fill="#555" dominantBaseline="middle" style={{ pointerEvents: 'none' }}>{v}</text>
              </g>
            );
          })}

          {/* Player A (always visible) */}
          {renderPlayerLine(entriesA, allEntriesA, visibleDotsA, animLineIdxA, xOfA, 'a', dataA.player_name, false)}

          {/* Player B (only when comparing) */}
          {isCompare && dataB && entriesB.length > 0 && (
            renderPlayerLine(entriesB, allEntriesB, visibleDotsB, animLineIdxB, xOfB, 'b', dataB.player_name, true)
          )}
        </svg>
      </div>

      {tooltip && (
        <div className="elo-chart-tooltip" style={{
          left: tooltip.x, top: tooltip.y,
          transform: 'translate(-50%, -100%)',
          position: 'fixed', zIndex: 1000,
        }} onClick={(ev) => ev.stopPropagation()}>
          <div className="elo-chart-tooltip-header">
            {tooltip.entry.opponent_avatar
              ? <img src={tooltip.entry.opponent_avatar} alt="" className="elo-chart-tooltip-avatar" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              : <div className="elo-chart-tooltip-avatar elo-chart-tooltip-avatar--bot"><svg viewBox="0 0 640 512" width="18" height="18"><path fill="#888" d="M352 0c0-17.7-14.3-32-32-32S288-17.7 288 0l0 64-96 0c-53 0-96 43-96 96l0 224c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-224c0-53-43-96-96-96l-96 0 0-64zM160 368c0-13.3 10.7-24 24-24l32 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-32 0c-13.3 0-24-10.7-24-24zm120 0c0-13.3 10.7-24 24-24l32 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-32 0c-13.3 0-24-10.7-24-24zm120 0c0-13.3 10.7-24 24-24l32 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-32 0c-13.3 0-24-10.7-24-24zM224 176a48 48 0 1 1 0 96 48 48 0 1 1 0-96zm144 48a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zM64 224c0-17.7-14.3-32-32-32S0 206.3 0 224l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96zm544-32c-17.7 0-32 14.3-32 32l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96c0-17.7-14.3-32-32-32z"/></svg></div>
            }
            <span className="elo-chart-tooltip-name">vs {tooltip.entry.opponent_name}</span>
          </div>
          <div className="elo-chart-tooltip-row">
            <span>{mode === 'rank' ? 'Rank' : 'OnFire'}</span>
            <span>
              {mode === 'rank'
                ? `#${tooltip.entry.rank_before} → #${tooltip.entry.rank_after}`
                : `${tooltip.entry.elo_before} → ${tooltip.entry.elo_after}`
              }
              <strong className={
                mode === 'rank'
                  ? (tooltip.entry.rank_after < tooltip.entry.rank_before ? 'elo-tooltip-win' : tooltip.entry.rank_after > tooltip.entry.rank_before ? 'elo-tooltip-loss' : '')
                  : (tooltip.entry.result === 'win' ? 'elo-tooltip-win' : tooltip.entry.result === 'loss' ? 'elo-tooltip-loss' : '')
              }>
                {' '}({
                  mode === 'rank'
                    ? `#${tooltip.entry.rank_after}`
                    : `${tooltip.entry.elo_change > 0 ? '+' : ''}${tooltip.entry.elo_change}`
                })
              </strong>
            </span>
          </div>
          <div className="elo-chart-tooltip-row">
            <span>Rank</span>
            <span>
              #{tooltip.entry.rank_before} → #{tooltip.entry.rank_after}
              <strong className={tooltip.entry.rank_after < tooltip.entry.rank_before ? 'elo-tooltip-win' : tooltip.entry.rank_after > tooltip.entry.rank_before ? 'elo-tooltip-loss' : ''}>
                {' '}({tooltip.entry.rank_before - tooltip.entry.rank_after > 0 ? '↑' : tooltip.entry.rank_before - tooltip.entry.rank_after < 0 ? '↓' : '='}
                {Math.abs(tooltip.entry.rank_before - tooltip.entry.rank_after)})
              </strong>
            </span>
          </div>
          <div className="elo-chart-tooltip-row">
            <span>Result</span>
            <span>{tooltip.entry.result === 'win' ? '🏆 Victory' : tooltip.entry.result === 'loss' ? '😞 Defeat' : '🤝 Draw'}</span>
          </div>
          <div className="elo-chart-tooltip-row elo-chart-tooltip-date">
            {new Date(tooltip.entry.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        </div>
      )}
    </div>
  );
}
